import classNames from 'classnames';
import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { getGlobalStyleSheets } from '../../global-styles';
import { Sizes, enumAttributeConverter, SizesValuesMap } from '../enum-types';
import { AtlFormFileUploadProps } from './form.props';

import * as TagNames from './form.tagnames';
import { AtlFormFileUploadEvent } from './form.events';

@customElement(TagNames.FormFileUpload)
export class AtlFormFileUpload
   extends LitElement
   implements AtlFormFileUploadProps
{
   declare inputId: string;
   declare inputClassName?: string;
   declare size?: Sizes;
   declare files?: File;
   declare hideDefault?: boolean;
   declare placeholder?: string;
   declare accept?: string[];
   declare protected value?: string;
   @query('slot') private _slot!: HTMLSlotElement;

   static properties = {
      inputId: { type: String },
      inputClassName: { type: String },
      size: {
         type: String,
         convertor: enumAttributeConverter(Sizes, Sizes.Medium),
      },
      files: { type: Object },
      hideDefault: { type: Boolean },
      placeholder: { type: String },
      accept: { type: Array },
      value: { type: String, state: true },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
            .file-up-cont{
                display: flex;
                flex: 0 0 100%;
            }    
            .file-up-input{
                height: 38px;
                text-overflow: ellipsis;
                border-radius: 0px 5px 5px 0px;
                background-color: white !important;
                cursor: pointer;
                flex: 0 1 100%;
            }    
            .file-up-clear{
                position: relative;
                right: 25px;
                margin-top: 12px;
                cursor: pointer;
            }
        }`,
   ];

   protected firstUpdated(_changedProperties: PropertyValues): void {
      super.firstUpdated(_changedProperties);

      if (this.files) {
         this.value = this.files.name;
      }
   }

   renderClear() {
      if (!this.value) {
         return nothing;
      }

      return html`
         <i
            class="fa-solid fa-xmark file-up-clear"
            part="icon"
            @click=${() => this._clearFile('')}
         ></i>
      `;
   }

   renderInput() {
      if (this.hideDefault) {
         return nothing;
      }

      return html`
         <input
            id=${this.inputId}
            class=${classNames(
               'form-control',
               this.inputClassName,
               SizesValuesMap[this.size ?? Sizes.Medium],
               'file-up-input'
            )}
            title=${ifDefined(this.value)}
            type="text"
            .value="${this.value ?? ''}"
            readonly
            @click=${this._handleKeyUp}
            placeholder=${ifDefined(this.placeholder)}
         />
      `;
   }

   render() {
      return html`
         <div class="file-up-cont">
            <div class="file-up-cont">
               <label id="${this.inputId}-label" for=${this.inputId} @click=${this._handleLabelClick}>
                  <slot></slot>
               </label>
               <input
                  id=${this.inputId}
                  class=${classNames(
                     'form-control visually-hidden',
                     this.inputClassName,
                     SizesValuesMap[this.size ?? Sizes.Medium]
                  )}
                  title=${this.title}
                  type="file"
                  accept="${ifDefined(this.accept?.join(','))}"
                  @change=${this._handleChange}
               />
               ${this.renderInput()}
            </div>
            ${this.renderClear()}
         </div>
      `;
   }

   private _handleLabelClick = (e: MouseEvent) => {
      if (!this._slot) return;

      const path = e.composedPath();
      const clickedInSlot = path.includes(this._slot);

      if (clickedInSlot) {
         e.preventDefault();
         const input = this.renderRoot?.querySelector(
            `#${this.inputId}-label`
         ) as HTMLInputElement;
         input?.click();
      }
   };

   private _handleKeyUp = () => {
      const input = this.renderRoot?.querySelector(
         `#${this.inputId}`
      ) as HTMLInputElement;
      input.click();
   };

   private _handleChange = (event: Event) => {
      const files = (event.target as HTMLInputElement)?.files;
      if (files && files.length > 0) {
         this.value = files[0].name;
         this.dispatchEvent(new AtlFormFileUploadEvent(files));
      }
   };

   private _clearFile = (value: string) => {
      this.value = undefined;
      const input = this.renderRoot?.querySelector(
         `#${this.inputId}`
      ) as HTMLInputElement;
      input.value = value;
      this.dispatchEvent(
         new CustomEvent('fileUpload', {
            detail: { payload: undefined },
            bubbles: true,
            composed: true,
         })
      );
   };
}
